import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { codeService } from "@/lib/code-service"
import { env } from "@/lib/env"
import { logger } from "@/lib/logger"
import type { GroupedCodeValues } from "@/types/code-values"

declare global {
  interface Window {
    PhoenixCodeValues?: GroupedCodeValues
  }
}

type CodeContextType = {
  isLoading: boolean
  error: string | null
}

const CodeContext = createContext<CodeContextType | undefined>(undefined)

export function CodeProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const [isLoading, setIsLoading] = useState(
    () => !(typeof window !== "undefined" && window.PhoenixCodeValues) && !env.useMockData
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Already provided by host (embedded in Phoenix)
    if (typeof window !== "undefined" && window.PhoenixCodeValues) {
      return
    }

    // Mock mode: decision tables are used directly, no code values needed
    if (env.useMockData) {
      return
    }

    // Real API: wait for auth then fetch
    if (!isAuthenticated) {
      return
    }

    setIsLoading(true)
    setError(null)

    codeService
      .getCodeValues(token)
      .then((grouped) => {
        if (typeof window !== "undefined") {
          window.PhoenixCodeValues = grouped
        }
      })
      .catch((err: unknown) => {
        logger.error(err)
        const message = err instanceof Error ? err.message : "Failed to load code values"
        setError(message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isAuthenticated, token])

  return (
    <CodeContext.Provider value={{ isLoading, error }}>
      {children}
    </CodeContext.Provider>
  )
}

export function useCode() {
  const context = useContext(CodeContext)
  if (context === undefined) {
    throw new Error("useCode must be used within a CodeProvider")
  }
  return context
}
